/**
 * Deterministic avatar fallback helpers used wherever a user/wallet has
 * no profile picture. Same key in → same gradient + initials out, so
 * every wallet keeps a recognisable visual handle across the app.
 *
 * Used by:
 *   - TraderCardCompact / TraderCard (post composer + post render)
 *   - PitHeader (similar pattern, kept inline for now)
 *   - any future card that needs a non-photo identity tile
 */

const GRADIENTS = [
  "from-purple-500 via-fuchsia-500 to-pink-500",
  "from-emerald-500 via-teal-500 to-cyan-500",
  "from-amber-500 via-orange-500 to-rose-500",
  "from-blue-500 via-indigo-500 to-violet-500",
  "from-lime-500 via-green-500 to-emerald-500",
  "from-rose-500 via-red-500 to-orange-500",
  "from-sky-500 via-blue-500 to-indigo-500",
  "from-yellow-500 via-amber-500 to-orange-500",
] as const;

/**
 * Pick a Tailwind `bg-gradient-to-br` class string deterministically
 * keyed on the input. Plain `String#charCodeAt` sum keeps this stable
 * across SSR / client renders.
 */
export function gradientForKey(key: string | null | undefined): string {
  if (!key) return GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return GRADIENTS[hash % GRADIENTS.length];
}

/**
 * Initials for the gradient tile. For wallet addresses (0x...) we drop
 * the `0x` prefix and use the last 2 hex chars uppercased — that's the
 * commonly-seen visual fingerprint and avoids the ugly "0" we'd get
 * from blindly taking the first character.
 */
export function initialsForKey(name: string | null | undefined): string {
  if (!name) return "??";
  const trimmed = name.trim();
  // Wallet → last 2 hex chars uppercased (so 0xfa3C…0e8C ⇒ "8C")
  if (/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
    return trimmed.slice(-2).toUpperCase();
  }
  // Truncated wallet shape from ourselves: 0x.....abcd ⇒ "AB"
  if (/^0x[a-fA-F0-9]+[…\.]+[a-fA-F0-9]+$/.test(trimmed)) {
    return trimmed.slice(-2).toUpperCase();
  }
  // Name → first letter of each of the first two words
  const parts = trimmed.split(/[\s_-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  // Single word → first 2 chars
  return trimmed.slice(0, 2).toUpperCase();
}
